import React, { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../api/api';

const DEFAULT_OPENAI_MODEL = 'gpt-5.4';

const MODEL_TEST_FAILED_MESSAGE = 'Model test failed';
const MODEL_TEST_NETWORK_ERROR_MESSAGE = 'Could not reach the server to test the model';

const OPENAI_MODELS = [
  { value: 'gpt-6-astra',   label: 'GPT-6 Astra (Most capable)' },
  { value: 'gpt-6.1-sol',   label: 'GPT-6.1 Sol' },
  { value: 'gpt-6.1-terra', label: 'GPT-6.1 Terra' },
  { value: 'gpt-6-sol',     label: 'GPT-6 Sol' },
  { value: 'gpt-6-luna',    label: 'GPT-6 Luna (Most efficient)' },
  { value: 'gpt-5.6-sol',   label: 'GPT-5.6 Sol' },
  { value: 'gpt-5.6-terra', label: 'GPT-5.6 Terra' },
  { value: 'gpt-5.6-luna',  label: 'GPT-5.6 Luna' },
  { value: 'gpt-5.5',       label: 'GPT-5.5' },
  { value: 'gpt-5.4',       label: 'GPT-5.4' },
  { value: 'gpt-5.4-mini',  label: 'GPT-5.4 Mini' },
  { value: 'gpt-5.4-nano',  label: 'GPT-5.4 Nano' },
  { value: 'gpt-5.2',       label: 'GPT-5.2' },
  { value: 'gpt-5.1',       label: 'GPT-5.1' },
  { value: 'gpt-5',         label: 'GPT-5' },
  { value: 'gpt-5-mini',    label: 'GPT-5 Mini' },
  { value: 'gpt-5-nano',    label: 'GPT-5 Nano' },
  { value: 'gpt-4.1',       label: 'GPT-4.1' },
  { value: 'gpt-4.1-mini',  label: 'GPT-4.1 Mini' },
  { value: 'gpt-4o',        label: 'GPT-4o' },
  { value: 'gpt-4o-mini',   label: 'GPT-4o Mini' },
  { value: 'o3',            label: 'o3 (Reasoning)' },
];

const SettingsPage = () => {
  const [settings, setSettings] = useState({
    smtp_settings: null,
    openai_api_key: null,
    interview_email_template: null
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [activeTab, setActiveTab] = useState('smtp');

  // Form states
  const [smtpForm, setSmtpForm] = useState({
    smtp_server: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: true,
    smtp_use_ssl: false,
    from_name: '',
    from_email: ''
  });

  const [emailForm, setEmailForm] = useState({
    subject: '',
    body: ''
  });

  const [openaiKey, setOpenaiKey] = useState('');
  const [openaiModel, setOpenaiModel] = useState(DEFAULT_OPENAI_MODEL);
  const [isTestingModel, setIsTestingModel] = useState(false);
  const [modelTestResult, setModelTestResult] = useState(null);

  // Fetch settings function with useCallback to prevent re-renders
  const fetchSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/settings/`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch settings');
      }
      
      const data = await response.json();
      setSettings(data);

      // Populate forms
      if (data.smtp_settings) {
        setSmtpForm(data.smtp_settings);
      }
      
      if (data.interview_email_template) {
        setEmailForm(data.interview_email_template);
      }
      
      if (data.openai_api_key) {
        setOpenaiKey(data.openai_api_key);
      }

      if (data.openai_model) {
        setOpenaiModel(data.openai_model);
      }

    } catch (err) {
      setError(`Failed to load settings: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  // Fetch settings on component mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const handleSMTPSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_URL}/settings/smtp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(smtpForm),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save SMTP settings');
      }

      setSuccess('SMTP settings saved successfully');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleTestSMTP = async () => {
    try {
      setTesting(true);
      setError(null);

      const response = await fetch(`${API_URL}/settings/test-smtp`, {
        method: 'POST',
      });

      const result = await response.json();

      if (result.success) {
        setSuccess('SMTP connection test successful!');
      } else {
        setError(result.message);
      }

    } catch (err) {
      setError(`SMTP test failed: ${err.message}`);
    } finally {
      setTesting(false);
    }
  };

  const handleEmailTemplateSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_URL}/settings/email-template`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(emailForm),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save email template');
      }

      setSuccess('Email template saved successfully');
      setTimeout(() => setSuccess(null), 3000);

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleOpenAISave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_URL}/settings/openai-key`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ openai_key: openaiKey }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save OpenAI API key');
      }

      setSuccess('OpenAI API key saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      
      // Refresh settings to show updated masked key
      await fetchSettings();

    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleModelSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await fetch(`${API_URL}/settings/openai-model`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ openai_model: openaiModel }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Failed to save model selection');
      }

      setSuccess('AI model saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleModelChange = (event) => {
    setOpenaiModel(event.target.value);
    setModelTestResult(null);
  };

  const handleModelTest = async () => {
    try {
      setIsTestingModel(true);
      setModelTestResult(null);

      const response = await fetch(`${API_URL}/settings/openai-model/test`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ openai_model: openaiModel }),
      });
      const data = await response.json();

      if (!response.ok) {
        setModelTestResult({ success: false, message: data.detail || MODEL_TEST_FAILED_MESSAGE });
        return;
      }

      setModelTestResult({ success: data.success, message: data.message, latencyMs: data.latency_ms });
    } catch (err) {
      setModelTestResult({ success: false, message: MODEL_TEST_NETWORK_ERROR_MESSAGE });
    } finally {
      setIsTestingModel(false);
    }
  };

  const loadDefaults = async () => {
    try {
      const response = await fetch(`${API_URL}/settings/defaults`);
      const data = await response.json();
      
      if (data.default_email_template) {
        setEmailForm(data.default_email_template);
      }
    } catch (err) {
      setError('Failed to load default template');
    }
  };

  const tabs = [
    { id: 'smtp', name: 'SMTP Settings', icon: '📧' },
    { id: 'email', name: 'Email Template', icon: '📝' },
    { id: 'openai', name: 'OpenAI API', icon: '🤖' }
  ];

  if (loading) {
    return (
      <div className="w-full px-4 py-10 flex justify-center">
        <svg className="animate-spin h-8 w-8 text-primary-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span className="ml-3 text-gray-600">Loading settings...</span>
      </div>
    );
  }

  return (
    <div className="w-full px-4">
      <div className="bg-white shadow-md rounded-lg p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Application Settings</h1>
          <p className="text-gray-600 mt-1">Configure SMTP, email templates, and API keys</p>
        </div>

        {/* Alerts */}
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-6">
            {success}
          </div>
        )}

        {/* Tabs */}
        <div className="flex space-x-1 mb-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`settings-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                activeTab === tab.id
                  ? 'bg-primary-100 text-primary-700 border border-primary-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <span className="mr-2">{tab.icon}</span>
              {tab.name}
            </button>
          ))}
        </div>

        {/* SMTP Settings Tab */}
        {activeTab === 'smtp' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">SMTP Configuration</h3>
              <p className="text-sm text-gray-600 mb-4">
                Configure your email server settings to send interview invitations.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Server
                </label>
                <input
                  type="text"
                  value={smtpForm.smtp_server}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, smtp_server: e.target.value }))}
                  placeholder="smtp.gmail.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Port
                </label>
                <input
                  type="number"
                  value={smtpForm.smtp_port}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  SMTP Username
                </label>
                <input
                  type="text"
                  value={smtpForm.smtp_username}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, smtp_username: e.target.value }))}
                  placeholder="username or your-email@gmail.com"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Username for SMTP authentication (may be different from sender email)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <input
                  type="password"
                  value={smtpForm.smtp_password}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, smtp_password: e.target.value }))}
                  placeholder="App password or email password"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Email Address
                </label>
                <input
                  type="email"
                  value={smtpForm.from_email}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, from_email: e.target.value }))}
                  placeholder="sender@company.com (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Email address that appears as sender. If empty, will use SMTP username.
                </p>
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  From Name (Display Name)
                </label>
                <input
                  type="text"
                  value={smtpForm.from_name}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, from_name: e.target.value }))}
                  placeholder="HR Team, Company Name, etc. (optional)"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Optional display name for outgoing emails. If provided, emails will appear as "Display Name &lt;email@domain.com&gt;"
                </p>
              </div>
            </div>

            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={smtpForm.smtp_use_tls}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, smtp_use_tls: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Use TLS</span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={smtpForm.smtp_use_ssl}
                  onChange={(e) => setSmtpForm(prev => ({ ...prev, smtp_use_ssl: e.target.checked }))}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-700">Use SSL</span>
              </label>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleSMTPSave}
                disabled={saving}
                className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                Save SMTP Settings
              </button>

              <button
                onClick={handleTestSMTP}
                disabled={testing || !settings.smtp_settings}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {testing ? (
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                Test Connection
              </button>
            </div>
          </div>
        )}

        {/* Email Template Tab */}
        {activeTab === 'email' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">Interview Email Template</h3>
              <p className="text-sm text-gray-600 mb-4">
                Customize the email template for interview invitations. Use variables like {`{{candidate_name}}`}, {`{{job_title}}`}, {`{{total_score}}`}.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject Line
              </label>
              <input
                type="text"
                value={emailForm.subject}
                onChange={(e) => setEmailForm(prev => ({ ...prev, subject: e.target.value }))}
                placeholder={`Interview Invitation - {{job_title}}`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email Body
              </label>
              <textarea
                value={emailForm.body}
                onChange={(e) => setEmailForm(prev => ({ ...prev, body: e.target.value }))}
                rows={12}
                placeholder={`Dear {{candidate_name}}...`}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
              />
            </div>

            <div className="bg-blue-50 p-4 rounded-md">
              <h4 className="text-sm font-medium text-blue-800 mb-2">Available Variables:</h4>
              <div className="grid grid-cols-2 gap-2 text-sm text-blue-700">
                <span>• {`{{candidate_name}}`} - Candidate's name</span>
                <span>• {`{{job_title}}`} - Job position title</span>
                <span>• {`{{total_score}}`} - Evaluation score percentage</span>
                <span>• {`{{candidate_email}}`} - Candidate's email</span>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleEmailTemplateSave}
                disabled={saving}
                className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {saving ? (
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                Save Template
              </button>

              <button
                onClick={loadDefaults}
                className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
              >
                Load Default Template
              </button>
            </div>
          </div>
        )}

        {/* OpenAI API Tab */}
        {activeTab === 'openai' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">OpenAI API Configuration</h3>
              <p className="text-sm text-gray-600 mb-4">
                Configure your OpenAI API key and model for AI-powered CV evaluations.
              </p>
            </div>

            {/* API Key */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                OpenAI API Key
              </label>
              <input
                type="password"
                value={openaiKey}
                onChange={(e) => setOpenaiKey(e.target.value)}
                placeholder="sk-..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Your API key is encrypted and stored securely. Get your key from{' '}
                <a href="https://platform.openai.com/api-keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                  OpenAI Platform
                </a>.
              </p>
            </div>

            <button
              onClick={handleOpenAISave}
              disabled={saving}
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              Save API Key
            </button>

            <hr className="border-gray-200" />

            {/* Model Selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                AI Model
              </label>
              <select
                value={openaiModel}
                onChange={handleModelChange}
                data-testid="openai-model-select"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white"
              >
                {OPENAI_MODELS.map((m) => (
                  <option key={m.value} value={m.value}>
                    {m.label}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Model used for CV evaluation. Newer models are more capable but may cost more.
              </p>
            </div>

            <button
              onClick={handleModelSave}
              disabled={saving}
              className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {saving ? (
                <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : null}
              Save Model
            </button>

            <div className="flex items-center space-x-3">
              <button
                onClick={handleModelTest}
                disabled={isTestingModel}
                data-testid="openai-model-test-button"
                className="border border-primary-600 text-primary-700 px-4 py-2 rounded hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isTestingModel ? (
                  <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-testid="openai-model-test-spinner">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                ) : null}
                {isTestingModel ? 'Testing...' : 'Test Model'}
              </button>
              <span className="text-xs text-gray-500">
                Sends a small request to the selected model using the saved API key.
              </span>
            </div>

            {modelTestResult && (
              <div
                role="status"
                data-testid="openai-model-test-result"
                className={`rounded-md border px-4 py-3 text-sm ${
                  modelTestResult.success
                    ? 'border-green-200 bg-green-50 text-green-800'
                    : 'border-red-200 bg-red-50 text-red-800'
                }`}
              >
                <p className="font-medium">
                  {modelTestResult.success ? 'Model works' : 'Model test failed'}
                  {modelTestResult.latencyMs !== undefined ? ` (${modelTestResult.latencyMs} ms)` : ''}
                </p>
                <p>{modelTestResult.message}</p>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default SettingsPage;
